import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Base line chart configuration
export const getBaseLineChartOptions = (customOptions = {}) => {
  return {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        displayColors: true,
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      ...customOptions.plugins,
    },
    scales: {
      x: {
        title: { display: true },
        grid: { display: false },
        ...customOptions.scales?.x,
      },
      y: {
        title: { display: true },
        beginAtZero: true,
        grid: {
          opacity: 0.5,
          lineWidth: 0.3,
        },
        ...customOptions.scales?.y,
      },
    },
    layout: {
      padding: { left: -24 },
      ...customOptions.layout,
    },
    ...customOptions,
  };
};

// Base dataset configuration for smooth line charts without points
export const getBaseLineDataset = (label, data, color, customConfig = {}) => {
  return {
    label,
    data,
    fill: true,
    backgroundColor: color + "45",
    borderColor: color,
    tension: 0.4,
    pointRadius: 0, // Hide points by default
    pointHoverRadius: 6, // Show points on hover
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 2,
    ...customConfig,
  };
};

// Chart type configurations
export const CHART_CONFIGS = {
  currency: {
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
    },
  },
  standard: {},
};

// Base bar chart options
export const getBaseBarChartOptions = (customOptions = {}) => {
  return {
    responsive: true,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || "";
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      ...customOptions.plugins,
    },
    scales: {
      x: {
        grid: { display: false },
        ...customOptions.scales?.x,
      },
      y: {
        beginAtZero: true,
        grid: { opacity: 0.5, lineWidth: 0.3 },
        ...customOptions.scales?.y,
      },
    },
    ...customOptions,
  };
};

// Base bar dataset
export const getBaseBarDataset = (label, data, color, customConfig = {}) => {
  return {
    label,
    data,
    backgroundColor: color + "99",
    borderColor: color,
    borderWidth: 1,
    borderRadius: 4,
    ...customConfig,
  };
};

// Base pie chart options
export const getBasePieChartOptions = (customOptions = {}) => {
  return {
    responsive: true,
    plugins: {
      legend: { display: true, position: "bottom" },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.parsed;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      ...customOptions.plugins,
    },
    ...customOptions,
  };
};

// Helper function to format labels based on granularity
export const formatChartLabels = (timeStats, granularity) => {
  return timeStats.map((item) => {
    if (granularity === "yearly") {
      return item.year || "";
    } else if (granularity === "monthly") {
      if (!item.month) return "";
      const [year, month] = item.month.split("-");
      if (!year || !month) return item.month;
      const date = new Date(year, month - 1); // month is 0-indexed
      return date.toLocaleString("default", { month: "short" }); // e.g., "Jan"
    } else if (granularity === "daily") {
      if (!item.period) return "";
      const date = new Date(item.period);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }); // e.g., "Jan 1"
    } else if (granularity === "hourly") {
      if (!item.period) return "";
      const date = new Date(item.period);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }); // e.g., "12:00"
    }
    return item.period || "";
  });
};

// Helper function to get chart title based on metric and granularity
export const getChartTitle = (metric, granularity) => {
  const metricTitles = {
    sales: "Sales",
    rentals: "Rentals",
    impressions: "Impressions",
    impression_revenue: "Impression Revenue",
  };

  const granularityTitles = {
    daily: "Per Day",
    hourly: "Per Hour",
    monthly: "Per Month",
    yearly: "Per Year",
  };

  const metricTitle = metricTitles[metric] || metric;
  const granularityTitle = granularityTitles[granularity] || "Per Month";

  return `${metricTitle} ${granularityTitle}`;
};
